import { Circle, Marker, useGoogleMap } from "@react-google-maps/api";
import { type Clusterer } from "@react-google-maps/marker-clusterer";
import { debounce } from "lodash-es";
import { useCallback, useMemo, useRef } from "react";

import CustomInfoWindow from "commonComponents/entity/map/common/CustomInfoWindow";
import { type IMark, PinTypes } from "commonComponents/entity/map/Map.types";
import { GetIconPathForPinType } from "commonComponents/entity/map/mapUtilities";

interface ICustomMarkProps {
    mark: IMark;
    showInfoWindow: boolean;
    onMouseOver: (id: string) => void;
    onClick?: (e: google.maps.MapMouseEvent) => void;
    onInfoWindowClick?: () => void;
    onCloseClick: () => void;
    clusterer?: Clusterer;
    autoClose?: boolean;
}

const DOUBLE_TAP_DELAY = 300;

const CustomMark: React.FunctionComponent<ICustomMarkProps> = ({
    clusterer,
    mark,
    onCloseClick,
    onMouseOver,
    showInfoWindow,
    autoClose,
    onClick,
    onInfoWindowClick,
}) => {
    const debouncedOnClose = useMemo(() => debounce(onCloseClick, 100), [onCloseClick]);
    const map = useGoogleMap();
    const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleClick = useCallback(
        (e: google.maps.MapMouseEvent) => {
            if (mark.infoWindowContent !== undefined) {
                clickTimerRef.current = setTimeout(() => {
                    clickTimerRef.current = null;
                    onMouseOver(mark.id.toString());
                }, DOUBLE_TAP_DELAY);
            } else {
                onClick?.(e);
            }
        },
        [mark, onMouseOver, onClick],
    );

    const handleDblClick = useCallback(
        (e: google.maps.MapMouseEvent) => {
            if (clickTimerRef.current !== null) {
                clearTimeout(clickTimerRef.current);
                clickTimerRef.current = null;
            }
            if (map) {
                map.setZoom((map.getZoom() ?? 0) + 1);
                if (e.latLng) {
                    map.panTo(e.latLng);
                }
            }
        },
        [map],
    );

    if (mark.radius === undefined && mark.pinType !== PinTypes.None) {
        // if the mark represents a Marker:
        const isAnimated = mark.animate;
        const size = mark.size ?? { height: 32, width: 32 };
        return (
            <>
                <Marker
                    key={mark.id + (isAnimated ? "-animated" : "")}
                    title={mark.title}
                    position={mark.position}
                    icon={{
                        url: GetIconPathForPinType(mark.pinType),
                        scaledSize: new google.maps.Size(size.width, size.height),
                    }}
                    animation={isAnimated ? google.maps.Animation.BOUNCE : undefined}
                    clusterer={clusterer}
                    options={{ clickable: mark.clickable ?? true }}
                    onClick={handleClick}
                    onDblClick={handleDblClick}
                    onMouseOver={() => {
                        if (mark.infoWindowContent !== undefined) {
                            onMouseOver(mark.id.toString());
                        }
                    }}
                    onMouseOut={() => {
                        if (autoClose) {
                            debouncedOnClose();
                        }
                    }}
                />
                {showInfoWindow && mark.infoWindowContent !== undefined && (
                    <CustomInfoWindow
                        mark={mark}
                        onCloseClick={onCloseClick}
                        onClick={onInfoWindowClick}
                    />
                )}
            </>
        );
    } else {
        // else if the mark represents a Circle:
        const options: google.maps.CircleOptions = {
            strokeWeight: 0,
            fillColor: mark.radiusColor,
            clickable: false,
            draggable: false,
            editable: false,
        };

        return (
            <Circle
                key={mark.id}
                center={mark.position}
                radius={mark.radius !== undefined ? mark.radius : 0}
                options={options}
            />
        );
    }
};

export default CustomMark;
